import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Map, Source, Layer, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';

// Public Doctivity Mapbox token (same one the migration heat maps use).
const MAPBOX_TOKEN =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

// Teal ramp matching the state-level report map (buildMap colorAxis).
const COLOR_SCALE = ['#E8F5F1', '#B8E4D6', '#8AD3BC', '#5CC2A2', '#2FB088', '#1E9E77', '#00A887', '#00795F'];

// Rough center + zoom from the bounding box of the counties that have data, so a
// single-state dataset (e.g. PA) fills the frame instead of a tiny US-wide view.
function viewFromFeatures(features) {
  let minX = 180;
  let minY = 90;
  let maxX = -180;
  let maxY = -90;
  let seen = false;
  const scan = (c) => {
    if (typeof c[0] === 'number') {
      const [x, y] = c;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      seen = true;
    } else { c.forEach(scan); }
  };
  features.forEach((f) => { if (f.geometry) scan(f.geometry.coordinates); });
  if (!seen) return { longitude: -96, latitude: 38, zoom: 3.3 };
  const span = Math.max(maxX - minX, maxY - minY, 0.5);
  const zoom = Math.max(3, Math.min(9, Math.log2(360 / span) - 1.2));
  return { longitude: (minX + maxX) / 2, latitude: (minY + maxY) / 2, zoom };
}

/**
 * County-level choropleth for a report 'map' whose rows carry a county FIPS (geo_id).
 * Reuses the app's counties.geojson (keyed by GEOID) + Mapbox, so a #13 county view
 * shades the actual counties instead of aggregating up to states.
 */
export function ReportCountyMap({
  rows, valueKey, geoIdKey, countyKey, stateKey, valueLabel, height,
}) {
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get('maps/counties.geojson')
      .then((r) => { if (alive) setGeo(r.data); })
      .catch(() => { if (alive) setGeo(null); });
    return () => { alive = false; };
  }, []);

  // Sum value per 5-digit FIPS; track the max for normalization.
  const { lookup, max } = useMemo(() => {
    const l = {};
    let m = 0;
    (rows || []).forEach((r) => {
      const id = String(r[geoIdKey] == null ? '' : r[geoIdKey]).trim().padStart(5, '0');
      if (id.length !== 5) return;
      const v = Number(r[valueKey]) || 0;
      const cur = l[id] || { value: 0, county: r[countyKey], state: r[stateKey] };
      cur.value += v;
      l[id] = cur;
      if (cur.value > m) m = cur.value;
    });
    return { lookup: l, max: m };
  }, [rows, valueKey, geoIdKey, countyKey, stateKey]);

  const merged = useMemo(() => {
    if (!geo || !Object.keys(lookup).length) return null;
    const features = geo.features
      .filter((f) => lookup[f.properties && f.properties.GEOID])
      .map((f) => {
        const d = lookup[f.properties.GEOID];
        return {
          ...f,
          properties: {
            ...f.properties, value: d.value, county: d.county, state: d.state, t: max > 0 ? d.value / max : 0,
          },
        };
      });
    return { type: 'FeatureCollection', features };
  }, [geo, lookup, max]);

  const initialViewState = useMemo(() => (merged ? viewFromFeatures(merged.features) : null), [merged]);

  const fillColor = useMemo(() => [
    'interpolate', ['linear'], ['get', 't'],
    0, COLOR_SCALE[0], 0.2, COLOR_SCALE[2], 0.45, COLOR_SCALE[4], 0.7, COLOR_SCALE[6], 1, COLOR_SCALE[7],
  ], []);

  const [hover, setHover] = useState(null);
  const onMove = useCallback((e) => {
    const f = e.features && e.features[0];
    if (f) {
      setHover({
        longitude: e.lngLat.lng, latitude: e.lngLat.lat, ...f.properties,
      });
    } else { setHover(null); }
  }, []);
  const onLeave = useCallback(() => setHover(null), []);

  if (!merged || !initialViewState) return null;

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <Map
        initialViewState={initialViewState}
        style={{ width: '100%', height }}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle='mapbox://styles/mapbox/light-v11'
        interactiveLayerIds={['report-county-fills']}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        attributionControl={false}
      >
        <Source id='report-counties' type='geojson' data={merged}>
          <Layer id='report-county-fills' type='fill' paint={{ 'fill-color': fillColor, 'fill-opacity': 0.75 }} />
          <Layer id='report-county-lines' type='line' paint={{ 'line-color': '#ffffff', 'line-width': 0.5, 'line-opacity': 0.6 }} />
        </Source>
        {hover && (
          <Popup longitude={hover.longitude} latitude={hover.latitude} closeButton={false} closeOnClick={false} offset={8}>
            <div style={{ fontSize: 12 }}>
              <strong>{hover.county || hover.NAME}{hover.state ? `, ${hover.state}` : ''}</strong>
              <br />
              {valueLabel || 'Value'}: {(Number(hover.value) || 0).toLocaleString()}
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
}

ReportCountyMap.propTypes = {
  rows: PropTypes.array.isRequired,
  valueKey: PropTypes.string.isRequired,
  geoIdKey: PropTypes.string,
  countyKey: PropTypes.string,
  stateKey: PropTypes.string,
  valueLabel: PropTypes.string,
  height: PropTypes.number,
};

ReportCountyMap.defaultProps = {
  geoIdKey: 'geo_id',
  countyKey: 'county',
  stateKey: 'state',
  valueLabel: 'Volume',
  height: 420,
};
